/**
 * Runs Pop-Out Panel Content
 * 
 * Initiate and manage computational runs.
 * Per spec:
 * - Run history (chronological)
 * - Primary CTA: New Run
 * - Run types: Backtest, Optimization, WFA, Adaptive/Forward Test
 * 
 * Conceptual rule: Runs are actions, not destinations.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlay,
    faFlask,
    faCog,
    faLayerGroup,
    faChartLine,
    faHistory,
    faFolder,
} from '@fortawesome/free-solid-svg-icons';

interface RunsPopOutProps {
    onClose: () => void;
}

export const RunsPopOut: React.FC<RunsPopOutProps> = ({ onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Check if we're in a project context
    const projectMatch = location.pathname.match(/\/app\/lab\/project\/([^/]+)/);
    const projectId = projectMatch ? projectMatch[1] : null;

    const handleNavigate = (path: string) => {
        navigate(path);
        onClose();
    };

    if (!projectId) {
        return (
            <div className="p-4">
                <div className="text-center py-8">
                    <FontAwesomeIcon icon={faFolder} className="h-8 w-8 text-gray-600 mb-3" />
                    <p className="text-sm text-gray-400 mb-2">No project open</p>
                    <p className="text-xs text-gray-500">
                        Open a project to run experiments
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 space-y-6">
            {/* New Run CTA */}
            <button
                onClick={() => handleNavigate(`/app/lab/project/${projectId}/backtest`)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-500 transition-colors font-medium"
            >
                <FontAwesomeIcon icon={faPlay} className="h-4 w-4" />
                New Run
            </button>

            {/* Run Types */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faFlask} className="h-3.5 w-3.5 text-white" />
                    <h3 className="text-xs font-medium text-white uppercase tracking-wider">Run Types</h3>
                </div>
                <div className="space-y-1">
                    <button
                        onClick={() => handleNavigate(`/app/lab/project/${projectId}/backtest`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-left group"
                    >
                        <FontAwesomeIcon icon={faFlask} className="h-4 w-4 text-blue-500/70 group-hover:text-blue-400" />
                        <div className="flex-1">
                            <p className="text-sm text-gray-300 group-hover:text-white">Backtest</p>
                            <p className="text-xs text-gray-500">Single parameter test</p>
                        </div>
                    </button>
                    <button
                        onClick={() => handleNavigate(`/app/lab/project/${projectId}/optimize`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-left group"
                    >
                        <FontAwesomeIcon icon={faCog} className="h-4 w-4 text-amber-500/70 group-hover:text-amber-400" />
                        <div className="flex-1">
                            <p className="text-sm text-gray-300 group-hover:text-white">Optimization</p>
                            <p className="text-xs text-gray-500">Parameter search</p>
                        </div>
                    </button>
                    <button
                        onClick={() => handleNavigate(`/app/lab/project/${projectId}/wfa`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-left group"
                    >
                        <FontAwesomeIcon icon={faLayerGroup} className="h-4 w-4 text-emerald-500/70 group-hover:text-emerald-400" />
                        <div className="flex-1">
                            <p className="text-sm text-gray-300 group-hover:text-white">Walk-Forward Analysis</p>
                            <p className="text-xs text-gray-500">Robustness validation</p>
                        </div>
                    </button>
                    <button
                        onClick={() => handleNavigate(`/app/lab/project/${projectId}/forward`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-left group"
                    >
                        <FontAwesomeIcon icon={faChartLine} className="h-4 w-4 text-purple-500/70 group-hover:text-purple-400" />
                        <div className="flex-1">
                            <p className="text-sm text-gray-300 group-hover:text-white">Forward Test</p>
                            <p className="text-xs text-gray-500">Live simulation</p>
                        </div>
                    </button>
                </div>
            </div>

            {/* Run History */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faHistory} className="h-3.5 w-3.5 text-white" />
                    <h3 className="text-xs font-medium text-white uppercase tracking-wider">History</h3>
                </div>
                <button
                    onClick={() => handleNavigate(`/app/lab/project/${projectId}/history`)}
                    className="w-full text-center text-sm text-cyan-400 hover:text-cyan-300 transition-colors py-2"
                >
                    View run history
                </button>
            </div>
        </div>
    );
};

export default RunsPopOut;
